/**
 * <RecentCallsTable /> — per-portal audit-log feed per §4 / §6.
 *
 * Consumes GET /api/gov/audit-log filtered to one portal. Includes a
 * row-drill-in dialog that fetches the per-call payload via
 * GET /api/gov/audit-log/{id}/payload.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";
import type { GovAuditLogRow } from "../../lib/api";
import type { Portal } from "./HealthCard";

export interface RecentCallsTableProps {
  portal: Portal;
  legalEntityId: string;
}

export function RecentCallsTable({ portal, legalEntityId }: RecentCallsTableProps) {
  const { t } = useTranslation();
  const [drillIn, setDrillIn] = useState<GovAuditLogRow | null>(null);

  const logQ = useQuery({
    queryKey: ["gov-audit-log", portal, legalEntityId],
    queryFn: () => api.getGovAuditLog(legalEntityId, portal, 50),
    enabled: !!legalEntityId,
  });

  if (logQ.isLoading) {
    return (
      <p className="text-xs text-text-tertiary">
        {t("gov.loading", "Loading…")}
      </p>
    );
  }
  // Defensive: the endpoint always returns an array, but stub-fetches
  // in tests (or a 500 surfaced as `{}`) can yield non-array shapes.
  const rows: GovAuditLogRow[] = Array.isArray(logQ.data) ? logQ.data : [];
  if (rows.length === 0) {
    return (
      <p className="text-xs text-text-tertiary">
        {t(
          "gov.audit_log_empty_actionable",
          "No calls yet. Run a sandbox test below to populate this feed.",
        )}
      </p>
    );
  }

  return (
    <>
      <table className="w-full text-xs" data-testid={`recent-calls-${portal}`}>
        <thead className="text-text-tertiary">
          <tr>
            <th className="text-start px-2 py-1">
              {t("gov.col_endpoint", "Endpoint")}
            </th>
            <th className="text-start px-2 py-1">
              {t("gov.col_status", "Status")}
            </th>
            <th className="text-start px-2 py-1">
              {t("gov.col_duration", "Duration")}
            </th>
            <th className="text-start px-2 py-1">
              {t("gov.col_when", "When")}
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-border-subtle">
              <td className="px-2 py-1 font-mono">{row.endpoint}</td>
              <td className="px-2 py-1">
                <StatusPill status={row.status} />
              </td>
              <td className="px-2 py-1 text-text-secondary">
                {row.duration_ms != null ? `${row.duration_ms}ms` : "—"}
              </td>
              <td className="px-2 py-1 text-text-tertiary">
                {new Date(row.created_at)
                  .toISOString()
                  .slice(0, 16)
                  .replace("T", " ")}
              </td>
              <td className="px-2 py-1">
                <button
                  type="button"
                  onClick={() => setDrillIn(row)}
                  className="text-[10px] text-brand-blue hover:text-brand-deep"
                >
                  {t("gov.view_payload", "View")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {drillIn && (
        <PayloadDrillIn
          row={drillIn}
          legalEntityId={legalEntityId}
          onClose={() => setDrillIn(null)}
        />
      )}
    </>
  );
}

function StatusPill({ status }: { status: string }) {
  const color =
    status === "success"
      ? "bg-emerald-100 text-emerald-800"
      : status === "circuit_open"
      ? "bg-rose-100 text-rose-800"
      : status === "failure"
      ? "bg-amber-100 text-amber-800"
      : "bg-text-tertiary/10 text-text-tertiary";
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[9px] uppercase ${color}`}
    >
      {status}
    </span>
  );
}

function PayloadDrillIn({
  row,
  legalEntityId,
  onClose,
}: {
  row: GovAuditLogRow;
  legalEntityId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const payloadQ = useQuery({
    queryKey: ["gov-audit-payload", row.id, legalEntityId],
    queryFn: () => api.getGovAuditPayload(legalEntityId, row.id),
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-xl border border-border-subtle bg-surface-default p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">
            {row.endpoint}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-text-tertiary hover:text-text-primary"
          >
            {t("app.close", "Close")}
          </button>
        </div>
        {payloadQ.isLoading && (
          <p className="text-xs text-text-tertiary">
            {t("gov.loading", "Loading…")}
          </p>
        )}
        {payloadQ.data && (
          <div className="space-y-3 text-xs">
            <div>
              <h4 className="text-[10px] uppercase text-text-tertiary">
                {t("gov.payload_request", "Request")}
              </h4>
              <pre className="mt-1 overflow-x-auto rounded border border-border-subtle bg-surface-sunken p-2 font-mono text-[11px]">
                {JSON.stringify(payloadQ.data.request, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="text-[10px] uppercase text-text-tertiary">
                {t("gov.payload_response", "Response")}
              </h4>
              <pre className="mt-1 overflow-x-auto rounded border border-border-subtle bg-surface-sunken p-2 font-mono text-[11px]">
                {JSON.stringify(payloadQ.data.response, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecentCallsTable;
